import React, { useEffect, useState } from 'react';
import { fetchProfilesCountByCountryMonth } from '../services/api';
import TimeSeriesChart from '../components/TimeSeriesChart';
import Header from '../components/Header';
import FilterDropdown from '../components/FilterDropdown';
import { parseISO } from 'date-fns';
import { createSafeDateFromString } from '../utils/dateUtils';

const CountryTrendsDashboard = () => {
  const [rawData, setRawData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [yearlyData, setYearlyData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  
  // Country filter states
  const [selectedCountries, setSelectedCountries] = useState(new Set());
  const [countryStats, setCountryStats] = useState({});
  
  // Loading state
  const [loading, setLoading] = useState(true);

  // Calculate country statistics
  const calculateCountryStats = (data) => {
    const stats = {};
    data.forEach(({ users_count, country }) => {
      if (!stats[country]) {
        stats[country] = 0;
      }
      stats[country] += users_count;
    });
    return stats;
  };

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await fetchProfilesCountByCountryMonth();
        setRawData(data);
        
        // Calculate country statistics
        const stats = calculateCountryStats(data);
        setCountryStats(stats);
        
        // Calculate top 10 countries by total user count
        const sortedCountries = Object.entries(stats)
          .sort(([, countA], [, countB]) => countB - countA)
          .map(([country]) => country);
        const top10 = sortedCountries.slice(0, 10);
        
        // Set initial selected countries (all countries)
        setSelectedCountries(new Set(Object.keys(stats)));
        
        // Set initial filtered data (only top 10 countries)
        const initialFiltered = data.filter(({ country }) => top10.includes(country));
        setFilteredData(initialFiltered);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);


  // Transform data for yearly chart
  const transformYearlyData = (data) => {
    const yearly = {};
    
    data.forEach(({ users_count, country, month }) => {
      if (!month) return;
      
      const year = parseISO(month).getFullYear();
      const yearISO = `${year}-01-01`; // ISO date format for year (YYYY-MM-DD)
      const key = country;
      
      if (!yearly[yearISO]) {
        yearly[yearISO] = {};
      }
      if (!yearly[yearISO][key]) {
        yearly[yearISO][key] = 0;
      }
      yearly[yearISO][key] += users_count;
    });

    // Convert to Nivo format
    const series = {};
    Object.values(yearly).forEach(yearData => {
      Object.entries(yearData).forEach(([key, count]) => {
        if (!series[key]) {
          series[key] = [];
        }
      });
    });

    Object.entries(yearly).forEach(([yearISO, yearData]) => {
      Object.entries(yearData).forEach(([key, count]) => {
        series[key].push({ x: createSafeDateFromString(yearISO), y: count });
      });
    });

    return Object.entries(series).map(([id, data]) => ({
      id,
      data: data.sort((a, b) => a.x - b.x)
    }));
  };

  // Transform data for monthly chart
  const transformMonthlyData = (data) => {
    const monthly = {};
    
    data.forEach(({ users_count, country, month }) => {
      if (!month) return;
      
      // Extract date portion from datetime string (YYYY-MM-DD from "2025-06-01T00:00:00")
      const monthDate = month.split('T')[0];
      const key = country;
      
      if (!monthly[monthDate]) {
        monthly[monthDate] = {};
      }
      if (!monthly[monthDate][key]) {
        monthly[monthDate][key] = 0;
      }
      monthly[monthDate][key] += users_count;
    });

    // Convert to Nivo format
    const series = {};
    Object.values(monthly).forEach(monthData => {
      Object.entries(monthData).forEach(([key, count]) => {
        if (!series[key]) {
          series[key] = [];
        }
      });
    });

    Object.entries(monthly).forEach(([monthDate, monthData]) => {
      Object.entries(monthData).forEach(([key, count]) => {
        series[key].push({ x: createSafeDateFromString(monthDate), y: count });
      });
    });

    return Object.entries(series).map(([id, data]) => ({
      id,
      data: data.sort((a, b) => a.x - b.x)
    }));
  };

  // Filter data based on selected countries
  useEffect(() => {
    if (selectedCountries.size === 0) {
      setFilteredData([]);
    } else {
      // Get the top 10 countries from the selected countries
      const selectedCountriesArray = Array.from(selectedCountries);
      const selectedStats = {};
      selectedCountriesArray.forEach(country => {
        if (countryStats[country]) {
          selectedStats[country] = countryStats[country];
        }
      });
      
      // Sort selected countries by count and take top 10
      const sortedSelected = Object.entries(selectedStats)
        .sort(([, countA], [, countB]) => countB - countA)
        .slice(0, 10)
        .map(([country]) => country);
      
      // Filter raw data to only include top 10 of selected countries
      const filtered = rawData.filter(({ country }) => sortedSelected.includes(country));
      setFilteredData(filtered);
    }
  }, [selectedCountries, rawData, countryStats]);

  // Update chart data when filtered data changes
  useEffect(() => {
    if (filteredData.length > 0) {
      setYearlyData(transformYearlyData(filteredData));
      setMonthlyData(transformMonthlyData(filteredData));
    }
  }, [filteredData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading Country Trends Dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header pageTitle="WtW New User Country Trends Over Time: By Year and By Month" />

      {/* Country Filter Dropdown */}
      <div className="px-6 py-4">
        <FilterDropdown
          label="Country"
          items={countryStats}
          selectedItems={selectedCountries}
          onSelectionChange={setSelectedCountries}
        />
      </div>

      {/* Charts */}
      <div className="px-6 py-6 flex flex-col gap-12">
        {/* Yearly Chart */}
        <TimeSeriesChart
          data={yearlyData}
          title="New User Country Trends by Year"
          xAxisLabel="Year"
          yAxisLabel="# of Users/Year"
          height={400}
          showLegend={true}
          legendPosition="top-right"
          dateType="yearly"
        />

        {/* Monthly Chart */}
        <TimeSeriesChart
          data={monthlyData}
          title="New User Country Trends by Month"
          xAxisLabel="Month"
          yAxisLabel="# of Users/Month"
          height={400}
          showLegend={true}
          legendPosition="top-right"
          tickRotation={0}
          margin={{ top: 50, right: 110, bottom: 80, left: 60 }}
          dateType="monthly"
        />
      </div>
    </div>
  );
};

export default CountryTrendsDashboard;
